import { CameraOutlined, CloseCircleFilled } from '@ant-design/icons';
import { message, Upload, type UploadProps } from 'antd';
import { useField } from 'formik';
import { isObject } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type UploadFileWithPreviewFormDataFragment } from '../../api/fragments/UploadFileWithPreviewFormData';
import breakpoints from '../../utilities/breakpoints';
import { validateExtensions } from '../../utilities/extensions';
import FormItem, { type FormItemProps } from './FormItem';

const StyledUpload = styled(Upload)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 142px;
    width: 100%;

    & .ant-upload {
        font-size: 13px;
    }

    & .ant-upload.ant-upload-select-picture-card {
        margin: 0;
        width: 100%;
        height: 100%;
    }
`;

const PreviewImage = styled.div<{ $height: number }>`
    position: relative;
    border: 1px solid ${({ theme }) => theme.customerDetailsTheme.borderColor};
    line-height: 1;
    padding: 10px;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: ${({ $height }) => $height ?? 142}px;
    width: 100%;
`;

const StyledButton = styled.button`
    position: absolute;
    left: 10px;
    top: 15px;
    border: none;
    outline: none;
    cursor: pointer;
    color: ${({ theme }) => theme.customerDetailsTheme.ocrModalTheme.closeIconColor};
    background-color: transparent;
    z-index: 100;
`;

const StyledImg = styled.img<{ $objectFit?: React.CSSProperties['objectFit'] }>`
    width: 100%;
    height: 100%;
    object-fit: ${props => props.$objectFit ?? 'cover'};

    @media screen and (min-width: ${breakpoints.xxs}) and (max-width: ${breakpoints.lg}) {
        width: auto;
    }
`;

const StyledVideo = styled.video`
    width: 100%;
    height: 100%;
`;

export type ImageUploadFieldProps = Omit<UploadProps, 'accept' | 'listType' | 'maxCount'> & {
    name: string;
    extensions: string[];
    label?: string;
    required?: boolean;
    getLink?: (field: string, documentId: string) => Promise<string>;
    sizeLimitInMiB?: number;
    tooltip?: FormItemProps['tooltip'];
    placeHolderText?: string;
    imageFit?: React.CSSProperties['objectFit'];
    height?: number;
    help?: React.ReactNode;
};

const ImageUploadField = ({
    name,
    extensions,
    label,
    required = false,
    getLink,
    sizeLimitInMiB = 2,
    tooltip,
    placeHolderText,
    imageFit,
    height,
    help,
    disabled = false,
}: ImageUploadFieldProps) => {
    const { t } = useTranslation(['common']);

    const [, meta, helpers] = useField<File | UploadFileWithPreviewFormDataFragment>(name);
    const { initialValue } = meta;
    const [{ preview, isVideo }, setPreview] = useState<{ preview: string | ArrayBuffer | null; isVideo: boolean }>({
        preview: !(initialValue instanceof File) && isObject(initialValue) ? initialValue.url : null,
        isVideo: !(initialValue instanceof File) && isObject(initialValue) ? initialValue.url.includes('.mp4') : false,
    });

    const accept = useMemo(() => extensions.join(','), [extensions]);

    const customRequest = useCallback<UploadProps['customRequest']>(
        ({ file, onSuccess }) => {
            if (file instanceof File) {
                const reader = new FileReader();

                reader.onloadend = ({ target }) => {
                    setPreview({
                        preview: target?.result,
                        isVideo: file.type === 'video/mp4',
                    });
                };

                reader.readAsDataURL(file as Blob);

                // set the file manually
                helpers.setValue(file);
                // touch the field manually
                helpers.setTouched(true);

                // do nothing on success
                onSuccess(null);
            }
        },
        [helpers]
    );

    const onRemove = useCallback(() => {
        setPreview({
            preview: null,
            isVideo: false,
        });

        // manually touch the file
        helpers.setTouched(true);

        // delete the file
        helpers.setValue(null);
    }, [helpers]);

    const beforeUpload = useCallback<UploadProps['beforeUpload']>(
        file => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(true);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [sizeLimitInMiB, t]
    );

    const content = preview ? (
        <PreviewImage $height={height}>
            {!disabled && (
                <StyledButton onClick={() => onRemove()} type="button">
                    <CloseCircleFilled />
                </StyledButton>
            )}
            {isVideo && (
                <StyledVideo src={preview as string} controls>
                    <source src={preview as string} type="video/mp4" />
                </StyledVideo>
            )}
            {!isVideo && <StyledImg $objectFit={imageFit} alt="preview" src={preview as string} />}
        </PreviewImage>
    ) : (
        <StyledUpload
            accept={accept}
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            disabled={disabled}
            listType="picture-card"
            name={name}
            onRemove={onRemove}
            showUploadList={false}
        >
            <div>
                <CameraOutlined />
                <br />
                {placeHolderText}
            </div>
        </StyledUpload>
    );

    return (
        <FormItem help={help} label={label} meta={meta} name={name} required={required} tooltip={tooltip}>
            {content}
        </FormItem>
    );
};

export default ImageUploadField;
